import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useMerchantAnalyticsQuery } from '../api';
import { RecentActivity } from '../components/RecentActivity';
import { SalesBySourceCard } from '../components/SalesBySourceCard';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { KpiCard } from '@/features/dashboard/components/KpiCard';
import { StatusBreakdownBars } from '@/features/dashboard/components/StatusBreakdownBars';
import { formatCatalogPrice } from '@/features/products/format-price';
import { whenDefined } from '@/lib/optional-props';

const MONEY_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3v18M17 7.5c0-1.8-2.2-3-5-3s-5 1.2-5 3 2.2 3 5 3 5 1.2 5 3-2.2 3-5 3-5-1.2-5-3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const RECEIPT_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 3h12v18l-2.5-1.5L13 21l-2.5-1.5L8 21l-2-1.5V3Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M9 8h6M9 12h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CART_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 4h2l2.4 12.2a2 2 0 0 0 2 1.6h7.4a2 2 0 0 0 2-1.6L20.5 8H6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="21" r="1.4" fill="currentColor" />
    <circle cx="17" cy="21" r="1.4" fill="currentColor" />
  </svg>
);

const BOX_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3.5 8 12 3.5 20.5 8v8L12 20.5 3.5 16V8Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M3.5 8 12 12.5 20.5 8M12 12.5V20.5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

function formatPercent(rate: number): string {
  return new Intl.NumberFormat('fr-TN', { style: 'percent', maximumFractionDigits: 1 }).format(
    rate,
  );
}

/**
 * Analytics (S17) audit's own scope, and nothing beyond it: the merchant
 * KPI subset this environment's real transactional data can answer
 * honestly today (docs/résumée.txt, entrée à venir). Conversion, delivery
 * success and returns are absent, not "Bientôt" — no honest signal exists
 * for any of them yet (no order has ever reached DELIVERED/RETURNED in
 * this codebase's real history, no funnel data exists at all), and a
 * placeholder would still invent a KPI shape nothing asks for.
 *
 * `averageOrderValue`/`cancellationRate` are `number | null` — `null`
 * only when there is no order to divide by. Every other value, INCLUDING
 * a real `0`, renders explicitly (supervisor decision, this lot: "zero
 * is a value, not a 'soon' state" — never hide a real KPI for being
 * zero).
 */
export function AnalyticsPage(): ReactElement {
  const { t } = useI18n();
  const analyticsQuery = useMerchantAnalyticsQuery();

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('merchant.nav.analytics')}</h1>
        <p className="text-sm text-inkFaint">{t('merchant.analytics.subtitle')}</p>
      </div>

      {analyticsQuery.isPending ? (
        <TableSkeleton columns={4} />
      ) : analyticsQuery.isError ? (
        <EmptyState
          title={
            analyticsQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('merchant.analytics.loadError')
          }
          description={
            analyticsQuery.error.status === 403
              ? t('merchant.dashboard.accessDeniedDescription')
              : t(resolveErrorMessageKey(analyticsQuery.error))
          }
        />
      ) : (
        <Overview data={analyticsQuery.data} />
      )}
    </div>
  );
}

function Overview({
  data,
}: {
  data: ReturnType<typeof useMerchantAnalyticsQuery>['data'];
}): ReactElement | null {
  const { t } = useI18n();
  if (data === undefined) return null;

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label={t('merchant.analytics.revenue')}
          value={formatCatalogPrice(data.revenue)}
          hint={t('merchant.analytics.revenueHint', { count: data.revenueOrderCount })}
          icon={MONEY_ICON}
          tone="accent"
        />
        <KpiCard
          label={t('merchant.orders.title')}
          value={String(data.totalOrderCount)}
          icon={RECEIPT_ICON}
          tone="info"
        />
        <KpiCard
          label={t('merchant.analytics.avgOrderValue')}
          value={data.averageOrderValue === null ? '—' : formatCatalogPrice(data.averageOrderValue)}
          icon={CART_ICON}
          tone="good"
          {...whenDefined(
            'hint',
            data.averageOrderValue === null
              ? t('merchant.analytics.avgOrderValueEmptyHint')
              : undefined,
          )}
        />
        <KpiCard
          label={t('merchant.analytics.cancellationRate')}
          value={data.cancellationRate === null ? '—' : formatPercent(data.cancellationRate)}
          icon={RECEIPT_ICON}
          tone={
            data.cancellationRate !== null && data.cancellationRate > 0.1 ? 'critical' : 'neutral'
          }
          hint={
            data.cancellationRate === null
              ? t('merchant.analytics.cancellationRateNoOrders')
              : data.cancelledOrderCount === 0
                ? t('merchant.analytics.cancellationRateNoneCancelled')
                : t('merchant.analytics.cancellationRateCount', {
                    count: data.cancelledOrderCount,
                  })
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <KpiCard
          label={t('merchant.dashboard.kpiActiveProducts')}
          value={String(data.activeProductCount)}
          icon={BOX_ICON}
          tone="good"
        />
        <KpiCard
          label={t('merchant.analytics.stockAvailable')}
          value={String(data.stockAvailable)}
          icon={BOX_ICON}
        />
        <KpiCard
          label={t('merchant.analytics.stockReserved')}
          value={String(data.stockReserved)}
          icon={BOX_ICON}
        />
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-bold text-ink">
          {t('merchant.dashboard.statusByStatusTitle')}
        </h2>
        <div className="rounded-xl2 border border-line bg-surface p-5 shadow-card">
          <StatusBreakdownBars entries={data.ordersByStatus} />
        </div>
      </div>

      <SalesBySourceCard />

      <div>
        <h2 className="mb-3 font-display text-lg font-bold text-ink">
          {t('merchant.analytics.topProductsTitle')}
        </h2>
        {data.topProducts.length === 0 ? (
          <EmptyState
            title={t('merchant.analytics.topProductsEmptyTitle')}
            description={t('merchant.analytics.topProductsEmptyDescription')}
          />
        ) : (
          <Table caption={t('merchant.analytics.topProductsTitle')}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('merchant.products.table.colProduct')}</TableHeaderCell>
                <TableHeaderCell>{t('merchant.products.form.skuLabel')}</TableHeaderCell>
                <TableHeaderCell align="end">
                  {t('merchant.analytics.table.colQuantitySold')}
                </TableHeaderCell>
                <TableHeaderCell align="end">{t('merchant.analytics.revenue')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.topProducts.map((product) => (
                <TableRow key={product.productId}>
                  <TableCell>{product.productName}</TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-inkFaint">{product.sku}</span>
                  </TableCell>
                  <TableCell align="end">{product.quantitySold}</TableCell>
                  <TableCell align="end">{formatCatalogPrice(product.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <RecentActivity />
    </>
  );
}
